import Navigation from "@/components/Navigation";
import Breadcrumbs from "@/components/Breadcrumbs";
import Footer from "@/components/Footer";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Building2, Users, DollarSign, FileSpreadsheet, ShoppingCart, Shirt, Factory, Database, Package, Truck } from "lucide-react";
import { Link } from "react-router-dom";
import { useSEO } from "@/hooks/useSEO";
import RelatedSolutionsSection from "@/components/RelatedSolutionsSection";

const Wholesale = () => {
  useSEO({
    title: 'B2B Wholesale Commerce Platform | Ommni',
    description: 'Streamline wholesale operations with automated catalog distribution, tiered pricing, bulk ordering, and customer-specific pricing. Built for B2B and wholesale businesses.',
    keywords: 'wholesale commerce, B2B platform, wholesale management, tiered pricing, bulk ordering',
    canonical: 'https://ommni.io/solutions/wholesale',
  });

  const features = [
    {
      icon: FileSpreadsheet,
      title: "Automated Catalog Distribution",
      description: "Push product catalogs to wholesale customers automatically with custom formatting and branding.",
    },
    {
      icon: DollarSign,
      title: "Tiered Pricing Management",
      description: "Manage complex pricing structures with volume discounts and customer-specific pricing rules.",
    },
    {
      icon: Users,
      title: "Customer Portal Access",
      description: "Give wholesale customers real-time access to inventory, pricing, and order history.",
    },
    {
      icon: Building2,
      title: "Multi-Warehouse Visibility",
      description: "Show available inventory across all your distribution centers to optimize fulfillment.",
    },
  ];

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "B2B Wholesale Commerce Platform",
    "description": "Empower your B2B operations with automated workflows and real-time data synchronization. Automated catalog distribution, tiered pricing, bulk ordering, and customer-specific pricing.",
    "serviceType": "B2B Wholesale Commerce Platform",
    "provider": {
      "@type": "Organization",
      "name": "Ommni",
      "url": "https://ommni.io"
    },
    "areaServed": {
      "@type": "Place",
      "name": "Global"
    },
    "url": "https://ommni.io/solutions/wholesale"
  };

  return (
    <div className="min-h-screen">
      <Navigation />
      <main className="pt-32 px-4 pb-24">
        {/* JSON-LD Structured Data for Service */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
        />
        
        <div className="container mx-auto max-w-7xl">
          {/* Hero Section */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-primary/10 mb-6">
              <Building2 className="h-10 w-10 text-primary" />
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Wholesale & Distribution Solutions
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
              Empower your B2B operations with automated workflows and real-time data synchronization
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Button variant="cta" size="lg" asChild>
                <Link to="/contact">Get Started</Link>
              </Button>
              <Button variant="outline" size="lg" asChild>
                <Link to="/pricing">View Pricing</Link>
              </Button>
            </div>
          </div>

          {/* Features Grid */}
          <div className="grid md:grid-cols-2 gap-8 mb-16">
            {features.map((feature, index) => (
              <Card key={index} className="p-8 gradient-card">
                <div className="mb-4">
                  <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                    <feature.icon className="h-6 w-6 text-primary" />
                  </div>
                </div>
                <h3 className="text-2xl font-bold mb-3">{feature.title}</h3>
                <p className="text-muted-foreground">{feature.description}</p>
              </Card>
            ))}
          </div>

          {/* Powered By Section */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-center mb-4">
              Powered By Ommni Features
            </h2>
            <p className="text-muted-foreground text-center mb-12 max-w-2xl mx-auto">
              B2B-focused solutions built on our core platform capabilities
            </p>
            <div className="grid md:grid-cols-3 gap-6">
              <Card className="p-6 hover:shadow-lg transition-all hover:border-primary/50">
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                  <Database className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-lg font-bold mb-2">
                  <Link to="/features/pim" className="hover:text-primary transition-colors">
                    Product Information Management
                  </Link>
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Distribute catalogs to wholesale customers with automated formatting.
                </p>
              </Card>
              <Card className="p-6 hover:shadow-lg transition-all hover:border-primary/50">
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                  <Package className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-lg font-bold mb-2">
                  <Link to="/features/inventory" className="hover:text-primary transition-colors">
                    Inventory Management
                  </Link>
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Give customers real-time visibility across all your distribution centers.
                </p>
              </Card>
              <Card className="p-6 hover:shadow-lg transition-all hover:border-primary/50">
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                  <Truck className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-lg font-bold mb-2">
                  <Link to="/features/offers" className="hover:text-primary transition-colors">
                    Pricing & Offers
                  </Link>
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Manage tiered pricing and customer-specific pricing rules.
                </p>
              </Card>
            </div>
          </div>

          {/* Related Solutions */}
          <RelatedSolutionsSection
            solutions={[
              {
                icon: Factory,
                title: "Manufacturing",
                description: "Production to distribution management.",
                link: "/solutions/manufacturing",
                gradient: "bg-gradient-to-br from-orange-500/20 to-red-500/20",
              },
              {
                icon: ShoppingCart,
                title: "E-commerce & Retail",
                description: "Multi-channel retail operations.",
                link: "/solutions/ecommerce",
                gradient: "bg-gradient-to-br from-blue-500/20 to-cyan-500/20",
              },
              {
                icon: Shirt,
                title: "Fashion & Apparel",
                description: "Complex variant management for fashion.",
                link: "/solutions/fashion",
                gradient: "bg-gradient-to-br from-pink-500/20 to-rose-500/20",
              },
            ]}
          />

          {/* Benefits Section */}
          <div className="relative overflow-hidden rounded-lg border-2 bg-card p-12">
            <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-purple-500/5 to-background"></div>
            <div className="relative z-10">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold mb-4">
                  Scale Your B2B Operations
                </h2>
                <p className="text-muted-foreground max-w-2xl mx-auto">
                  Handle thousands of wholesale customers with automated processes that grow with your business
                </p>
              </div>
              <div className="grid md:grid-cols-3 gap-8 text-center">
                <div>
                  <div className="text-4xl font-bold text-primary mb-2">50%</div>
                  <p className="text-muted-foreground">Faster Order Processing</p>
                </div>
                <div>
                  <div className="text-4xl font-bold text-primary mb-2">75%</div>
                  <p className="text-muted-foreground">Reduced Manual Work</p>
                </div>
                <div>
                  <div className="text-4xl font-bold text-primary mb-2">24/7</div>
                  <p className="text-muted-foreground">Customer Self-Service</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Breadcrumbs />
      <Footer />
    </div>
  );
};

export default Wholesale;
